import { type MutableRefObject, useEffect } from 'react';

type UseLivenessTabVisibilityParams = {
    isDone: MutableRefObject<boolean>;
    abortPrepare: () => void;
    abortDetectLoop: () => void;
    skipPrepareCountdown: () => void;
    resumeCameraStream: () => Promise<boolean>;
    onTabVisible?: () => void;
    startDetectingRef: MutableRefObject<() => void>;
};

export const useLivenessTabVisibility = ({
    isDone,
    abortPrepare,
    abortDetectLoop,
    skipPrepareCountdown,
    resumeCameraStream,
    onTabVisible,
    startDetectingRef,
}: UseLivenessTabVisibilityParams) => {
    useEffect(() => {
        const handleVisibilityChange = () => {
            if (document.visibilityState === 'hidden') {
                abortPrepare();
                abortDetectLoop();

                return;
            }

            if (isDone.current) {
                return;
            }

            const resume = async () => {
                skipPrepareCountdown();

                const isVideoReady = await resumeCameraStream();

                if (isVideoReady) {
                    onTabVisible?.();
                }

                abortDetectLoop();
                startDetectingRef.current();
            };

            resume().catch(() => {});
        };

        document.addEventListener('visibilitychange', handleVisibilityChange);

        return () => {
            document.removeEventListener('visibilitychange', handleVisibilityChange);
        };
    }, [
        abortDetectLoop,
        abortPrepare,
        isDone,
        onTabVisible,
        resumeCameraStream,
        skipPrepareCountdown,
        startDetectingRef,
    ]);
};
